import {
  SUBSCRIPTION_DETAILS,
  SUBSCRIPTION_SELF_SERVE,
  UPGRADE_SUBSCRIPTION,
  USER_DETAIL,
} from "lib/constants";
import { getAPICall, postAPICall, putAPICall } from "lib/callApi";
import PropTypes from "prop-types";
import { createContext, useContext, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import useUserPilot from "hooks/useUserPilot";
import moment from "moment";
import { loginUserAction } from "redux/slice/users";
import { subscriptionActions } from "../redux/slice/subscription";

const WorkspaceContext = createContext();

export function SubscriptionProvider({ children, token }) {
  const userPilot = useUserPilot();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { subscription, loading, error } = useSelector((state) => state.subscription);

  // initial fetch of workspace subscription details
  const fetchWorkspaceSubscriptionDetails = async () => {
    try {
      dispatch(subscriptionActions.setSubscriptionLoading(true));
      if (token) {
        const result = await getAPICall(SUBSCRIPTION_DETAILS, true);
        if (result.data.success) {
          dispatch(subscriptionActions.setSubscriptionData(result.data.data));
        } else {
          dispatch(subscriptionActions.setSubscriptionError(result.data.message));
        }
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: "Something went wrong!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      dispatch(subscriptionActions.setSubscriptionLoading(false));
    }
  };

  const createSelfServeSubscription = async (plan) => {
    try {
      dispatch(subscriptionActions.setSubscriptionLoading(true));
      const payload = {
        subscriptionPlanId: plan.id,
        timezone: moment.tz.guess(),
      };

      const result = await postAPICall(SUBSCRIPTION_SELF_SERVE, true, payload);

      if (result?.data?.success) {
        userPilot.track("Free Trial Started");
        await fetchWorkspaceSubscriptionDetails();
        const refreshUserDetail = await getAPICall(USER_DETAIL, true);
        dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
        navigate("/home");
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const upgradeSubscription = async (plan) => {
    try {
      dispatch(subscriptionActions.setSubscriptionLoading(true));
      const payload = {
        subscriptionPlanId: plan.id,
        timezone: moment.tz.guess(),
      };

      const result = await putAPICall(UPGRADE_SUBSCRIPTION, true, payload);

      if (result?.data?.success) {
        userPilot.track("Free Trial Started");
        await fetchWorkspaceSubscriptionDetails();
        const refreshUserDetail = await getAPICall(USER_DETAIL, true);
        dispatch(loginUserAction.getLoginUserData(refreshUserDetail?.data?.data));
        navigate("/home");
      } else {
        const responseMessage = result?.data?.message || result?.response?.data?.message;
        enqueueSnackbar(
          JSON.stringify({
            icon: "info",
            title: "Information",
            text: responseMessage,
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "info",
          title: "Information",
          text: err.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(fetchWorkspaceSubscriptionDetails, [token]);

  const memoedValue = useMemo(
    () => ({
      subscription,
      isLoading: loading,
      refetchSubscription: fetchWorkspaceSubscriptionDetails,
      createSelfServeSubscription,
      upgradeSubscription,
      error,
    }),
    [subscription, loading, error, fetchWorkspaceSubscriptionDetails]
  );

  return <WorkspaceContext.Provider value={memoedValue}>{children}</WorkspaceContext.Provider>;
}

SubscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function useWorkspaceSubscription() {
  return useContext(WorkspaceContext);
}
